<template>
  <div class="d-flex flex-nowrap overflow-auto crisisTableContainer">
    <div class="p-1 w-100 crisis-report-panel">
      <div class="d-flex">
        <div class="d-flex">
          <h5>{{ $t('operational_security.crisis_form.allocations_by_region') }} -
            <span class="font-italic">{{ data.name }}-{{ data.reference_number }}</span>
          </h5>
        </div>
        <feather-icon @click="showFiltersFn" icon="FilterIcon" size="16" class="ml-1 cursor-pointer"/>
      </div>

      <div class="crisis-report-filters" v-if="showFilters">
        <h5>{{ $t('operational_security.crisis_form.filters_by_region_city') }}</h5>
        <div class="d-flex flex-wrap">
          <div v-for="(region, index) in selected" :key="region.region" class="crisis-report-filter-group">
            <b-form-checkbox
              :name="`option-filter-region${index}`"
              v-model="region.checked"
              :disabled="region.isDisabled"
              @change="onRegionFilterChange(region, $event)"
            >
              {{ region.region }}
            </b-form-checkbox>

            <div class="crisis-report-city-list" v-if="!region.checked">
              <b-form-checkbox
                v-for="city in region.cities"
                :key="city.key"
                :name="`option-filter-city${index}`"
                v-model="city.checked"
                :disabled="city.isDisabled"
              >
                {{ city.name  }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <h5>{{ $t("custom_forms.categories") }}</h5>
          <div class="metric-grid ml-1">
            <div
              v-for="(metric, index) in metricsFilters"
              :key="metric.name"
              class="metric-item"
            >
              <b-form-checkbox
                :name="`option-filter-metric${index}`"
                v-model="metric.checked"
                :disabled="metric.isDisabled"
              >
                {{ metric.name }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <b-form-checkbox
            name="Only rows with data"
            v-model="showOnlyRowsWithData"
          >
            {{  $t('operational_security.crisis_form.only_rows_with_data') }}
          </b-form-checkbox>
        </div>
      </div>

      <SkeletonLoading
        v-if="isMasterDataLoading"
        :count="1"
        :height="230"
        :fullWidth="true"
      />

      <div class="crisis-report-grid-shell">
        <crisis-report-grid
        v-if="!isMasterDataLoading && !dataNotExists"
        :source="gridRows"
        :columns="gridColumns"
        height="100%"
        />

        <div class="w-100 h-100 d-flex justify-content-center align-items-center" v-if="dataNotExists">
        <p class="text-center">No existe información para esta crisis</p>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import {computed, onMounted, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import { useCrisisFormUpdate } from "./composables/useCrisisFormUpdateSocket";
import CrisisReportGrid from './CrisisReportGrid.vue';
import {
  hasAnyRegionOrCitySelection,
  makeColumnGroup,
  makeGridColumn,
  makeRowHeaderColumn,
  makeSafeProp,
  onRegionSelectionChange,
  ROW_LABEL_PROP
} from './composables/useCrisisReportGrid';

export default {
  components: {
    CrisisReportGrid,
    SkeletonLoading
  },
  props: {
    data: {
      type: Object,
    },
    slotId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const { lastUpdate } = useCrisisFormUpdate(props.data.id);
    const masterData = ref([]);
    const showFilters = ref(false);
    const selected = ref([]);
    const metricsFilters = ref([]);
    const showOnlyRowsWithData = ref(false);
    const isMasterDataLoading = ref(false);
    const dataNotExists = ref(false);

    onMounted(() => {
      getAllocationByRegion();
    });

    watch(lastUpdate, () => getAllocationByRegion());

    const gridData = computed(() => {
      const columns = [makeRowHeaderColumn()];
      const rowsByMetric = {};
      const summaryTotals = {};
      const grouped = groupByRegion(masterData.value);
      const hasAnySelection = hasAnyRegionOrCitySelection(selected.value);
      const enabledMetrics = metricsFilters.value
        .filter(metric => metric.checked)
        .map(metric => metric.name);

      grouped.forEach(({ region, municipalities }) => {
        const selectedRegion = selected.value.find(item => item.region === region);
        if (!selectedRegion) return;

        let filteredMunicipalities = municipalities;
        if (hasAnySelection && !selectedRegion.checked) {
          filteredMunicipalities = municipalities.filter(municipality =>
            selectedRegion.cities.some(city =>
              city.key === municipality.municipality &&
              city.checked
            )
          );
        }

        if (!filteredMunicipalities.length) return;

        const metricMap = buildMetricMap(filteredMunicipalities, enabledMetrics);
        const metricNames = Object.keys(metricMap);
        if (!metricNames.length) return;

        const groupChildren = [];

        if (selectedRegion.checked) {
          const totalProp = makeSafeProp('region', region, 'total');
          groupChildren.push(makeGridColumn({
            prop: totalProp,
            name: 'Total',
            kind: 'total'
          }));

          metricNames.forEach(metricName => {
            const row = getMetricRow(rowsByMetric, metricName);
            row[totalProp] = metricMap[metricName].total;
            summaryTotals[metricName] = (summaryTotals[metricName] || 0) + metricMap[metricName].total;
          });
        } else {
          filteredMunicipalities.forEach(municipality => {
            const cityProp = makeSafeProp('region', region, municipality.municipality);
            groupChildren.push(makeGridColumn({
              prop: cityProp,
              name: municipality.municipality
            }));

            metricNames.forEach(metricName => {
              const row = getMetricRow(rowsByMetric, metricName);
              row[cityProp] = metricMap[metricName].values[municipality.municipality]?.total ?? 0;
            });
          });

          const totalProp = makeSafeProp('region', region, 'total');
          groupChildren.push(makeGridColumn({
            prop: totalProp,
            name: 'Total',
            kind: 'total'
          }));

          metricNames.forEach(metricName => {
            const row = getMetricRow(rowsByMetric, metricName);
            row[totalProp] = metricMap[metricName].total;
            summaryTotals[metricName] = (summaryTotals[metricName] || 0) + metricMap[metricName].total;
          });
        }

        columns.push(makeColumnGroup(region, groupChildren));
      });

      const rows = filterRowsWithData(Object.values(rowsByMetric));
      const visibleRows = showOnlyRowsWithData.value ? rows : Object.values(rowsByMetric);

      if (visibleRows.length) {
        const summaryProp = makeSafeProp('region', 'resumen', 'total');
        columns.push(makeColumnGroup('Resumen', [
          makeGridColumn({
            prop: summaryProp,
            name: 'Total',
            kind: 'summary'
          })
        ]));

        visibleRows.forEach(row => {
          row[summaryProp] = summaryTotals[row[ROW_LABEL_PROP]] || 0;
        });
      }

      return {
        columns,
        rows: visibleRows
      };
    });

    const gridColumns = computed(() => gridData.value.columns);
    const gridRows = computed(() => gridData.value.rows);

    async function getAllocationByRegion() {
      try {
        isMasterDataLoading.value = true;
        dataNotExists.value = false;
        const {data} = await axios.get(`/v1/operational-security-crisis/${props.data.id}/state-report`);
        masterData.value = data.data || [];
        selected.value = groupByRegion(masterData.value).map(region => ({
          region: region.region,
          checked: false,
          isDisabled: false,
          cities: region.municipalities.map(municipality => ({
            key: municipality.municipality,
            name: municipality.municipality,
            checked: false,
            isDisabled: false
          }))
        }));

        setMetricsFilters();
      } catch (error) {
        dataNotExists.value = true;
      } finally {
        isMasterDataLoading.value = false;
      }
    }

    function buildMetricMap(municipalities, enabledMetrics) {
      const metricMap = {};

      municipalities.forEach(municipality => {
        const metrics = enabledMetrics.length === 0
          ? municipality.data
          : municipality.data.filter(item =>
            enabledMetrics.some(metric => item.metric.includes(metric))
          );

        metrics.forEach(item => {
          if (!metricMap[item.metric]) {
            metricMap[item.metric] = {
              values: {},
              total: 0
            };
          }

          const total = item.total ?? 0;
          metricMap[item.metric].values[municipality.municipality] = { total };
          metricMap[item.metric].total += total;
        });
      });

      return metricMap;
    }

    function getMetricRow(rowsByMetric, metricName) {
      if (!rowsByMetric[metricName]) {
        rowsByMetric[metricName] = {
          [ROW_LABEL_PROP]: metricName
        };
      }

      return rowsByMetric[metricName];
    }

    function filterRowsWithData(rows) {
      return rows.filter(row =>
        Object.keys(row).some(key =>
          key !== ROW_LABEL_PROP &&
          Number(row[key]) > 0
        )
      );
    }

    function groupByRegion(data) {
      const map = {};

      data.forEach(item => {
        if (!map[item.region]) {
          map[item.region] = [];
        }

        map[item.region].push(item);
      });

      return Object.entries(map).map(([region, municipalities]) => ({
        region,
        municipalities
      }));
    }

    function showFiltersFn() {
      showFilters.value = !showFilters.value;
    }

    function setMetricsFilters() {
      const uniqueMetrics = [
        ...new Set(
          masterData.value.flatMap(item =>
            item.data.map(metricItem => metricItem.metric.split(' - ')[0])
          )
        )
      ];

      metricsFilters.value = uniqueMetrics.map(metric => ({
        name: metric,
        checked: false,
        isDisabled: false
      }));
    }

    function onRegionFilterChange(region, checked) {
      onRegionSelectionChange(region, checked);
    }

    return {
      selected,
      showFilters,
      gridColumns,
      gridRows,
      isMasterDataLoading,
      dataNotExists,
      metricsFilters,
      showOnlyRowsWithData,
      showFiltersFn,
      onRegionFilterChange
    };
  }
};
</script>

<style scoped lang="scss">
.crisisTableContainer {
  height: 100%;
  min-height: 0;
  background-color: #fff;
}

.crisis-report-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.crisis-report-grid-shell {
  flex: 1 1 auto;
  min-height: 420px;
  height: calc(100vh - 240px);
}

.crisis-report-filters {
  margin: 12px 0 16px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.crisis-report-filter-group {
  margin: 0 18px 14px 0;
}

.crisis-report-city-list {
  margin: 8px 0 0 16px;
}

.crisis-report-filter-section {
  margin-top: 14px;
}

.metric-grid {
  max-width: 650px;
  max-height: 150px;
  display: flex;
  flex-wrap: wrap;
}

.metric-item {
  width: 100px;
}
</style>
